/**
 * Demo data for the admin control centre.
 *
 * CLOSDD is still on localStorage, so a fresh browser shows an empty console.
 * This seeds a realistic looking book of roles, candidates, decisions,
 * distributions and activity so the admin team can see the shape of the
 * product. It only runs when the store is effectively empty.
 */

import type { AdminStore, Distribution, DistributionStatus, PoolTag, Stage, Decision, ActivityEntry } from "./admin";
import type { Application, EmployerAccount, EmployerJob } from "./employer";

const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString();

type JobSeed = {
  id: string;
  ref: string;
  title: string;
  seniority: string;
  contract: string;
  company: string;
  anonymous: boolean;
  location: string;
  setting: string;
  pay: string;
  tags: string[];
  status: EmployerJob["status"];
  days: number;
};

const jobSeeds: JobSeed[] = [
  { id: "sdr-northline", ref: "CL-8F42", title: "Sales Development Representative", seniority: "Entry level", contract: "Full-time", company: "Northline Group", anonymous: false, location: "London, UK", setting: "Hybrid", pay: "£30,000 base + uncapped commission (OTE £48,000)", tags: ["urgent", "saas", "b2b", "uncapped-commission", "hybrid"], status: "Live", days: 2 },
  { id: "ae-brightpath", ref: "CL-2K19", title: "Account Executive", seniority: "Mid-level", contract: "Full-time", company: "Brightpath Software", anonymous: false, location: "Manchester, UK", setting: "Remote", pay: "£45,000 base + commission (OTE £85,000)", tags: ["hiring-now", "saas", "remote", "warm-leads"], status: "Live", days: 4 },
  { id: "field-sales-vertex", ref: "CL-7Q08", title: "Field Sales Executive", seniority: "Mid-level", contract: "Full-time", company: "Vertex Facilities", anonymous: false, location: "Birmingham, UK", setting: "On site", pay: "£32,000 base + car allowance (OTE £55,000)", tags: ["immediate-start", "b2b", "company-car"], status: "Live", days: 6 },
  { id: "bdm-anon-fintech", ref: "CL-5D77", title: "Business Development Manager", seniority: "Senior", contract: "Full-time", company: "Confidential fintech", anonymous: true, location: "London, UK", setting: "Hybrid", pay: "£60,000 base + commission (OTE £110,000)", tags: ["fintech", "b2b", "uncapped-commission", "senior"], status: "Live", days: 8 },
  { id: "sales-manager-orbit", ref: "CL-9T31", title: "Sales Manager", seniority: "Senior", contract: "Full-time", company: "Orbit Media", anonymous: false, location: "Leeds, UK", setting: "Hybrid", pay: "£55,000 base + team bonus (OTE £90,000)", tags: ["leadership", "media", "hybrid"], status: "Live", days: 11 },
  { id: "closer-apex", ref: "CL-4R63", title: "High Ticket Closer", seniority: "Mid-level", contract: "Contract", company: "Apex Coaching", anonymous: false, location: "Remote, UK", setting: "Remote", pay: "Commission only, average £7,000 per month", tags: ["commission-only", "remote", "closing-soon"], status: "Live", days: 13 },
  { id: "inside-sales-lumen", ref: "CL-1B55", title: "Inside Sales Executive", seniority: "Entry level", contract: "Full-time", company: "Lumen Energy", anonymous: false, location: "Glasgow, UK", setting: "On site", pay: "£26,000 base + commission (OTE £40,000)", tags: ["multiple-openings", "b2c", "training-provided"], status: "Paused", days: 18 },
  { id: "enterprise-ae-halden", ref: "CL-6M24", title: "Enterprise Account Executive", seniority: "Senior", contract: "Full-time", company: "Halden Systems", anonymous: false, location: "London, UK", setting: "Hybrid", pay: "£75,000 base + commission (OTE £150,000)", tags: ["saas", "enterprise", "senior", "uncapped-commission"], status: "Closed", days: 26 },
];

const advertOf = (j: JobSeed) =>
  `We are hiring a ${j.title} to own pipeline end to end. You will work inbound enquiries plus your own outbound activity and carry a personal number.\n\nWe care about how you sell, not how your CV is formatted. Record a 60 second pitch and tell us why we should put you in front of our buyers.`;

export const demoJobs: EmployerJob[] = jobSeeds.map((j) => ({
  id: j.id,
  ref: j.ref,
  title: j.title,
  seniority: j.seniority,
  contract: j.contract,
  company: j.company,
  anonymous: j.anonymous,
  location: j.location,
  setting: j.setting,
  payStructure: j.pay.toLowerCase().startsWith("commission") ? "Commission only" : "Base salary + commission",
  pay: j.pay,
  advert: advertOf(j),
  tags: j.tags,
  status: j.status,
  createdAt: daysAgo(j.days),
}));

type PersonSeed = {
  name: string;
  location: string;
  years: string;
  voiceAnswers: number;
  hasPitch: boolean;
  jobs: string[];
  status: Application["status"];
  decision: Decision;
  stage: Stage;
};

const people: PersonSeed[] = [
  { name: "Jordan Miles", location: "London, UK", years: "4 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["ae-brightpath", "bdm-anon-fintech"], status: "Shortlisted", decision: "Approved", stage: "Put forward" },
  { name: "Amara Bello", location: "Manchester, UK", years: "2 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["ae-brightpath"], status: "New", decision: "Approved", stage: "Interviewed" },
  { name: "Ryan Doyle", location: "Remote, UK", years: "6 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["closer-apex", "enterprise-ae-halden"], status: "Shortlisted", decision: "Approved", stage: "Hired" },
  { name: "Priya Raman", location: "London, UK", years: "1 year in sales", voiceAnswers: 2, hasPitch: true, jobs: ["sdr-northline"], status: "New", decision: "Pending", stage: "New" },
  { name: "Tom Whitfield", location: "Birmingham, UK", years: "5 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["field-sales-vertex"], status: "Shortlisted", decision: "Approved", stage: "Put forward" },
  { name: "Chloe Berrington", location: "Leeds, UK", years: "8 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["sales-manager-orbit"], status: "New", decision: "Approved", stage: "Interviewed" },
  { name: "Marcus Ade", location: "London, UK", years: "3 years in sales", voiceAnswers: 1, hasPitch: false, jobs: ["sdr-northline", "ae-brightpath"], status: "New", decision: "Pending", stage: "New" },
  { name: "Sofia Kaur", location: "Glasgow, UK", years: "2 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["inside-sales-lumen"], status: "New", decision: "Approved", stage: "Approved" },
  { name: "Danny Okafor", location: "Manchester, UK", years: "7 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["bdm-anon-fintech", "enterprise-ae-halden"], status: "Shortlisted", decision: "Approved", stage: "Put forward" },
  { name: "Ellie Vance", location: "Remote, UK", years: "4 years in sales", voiceAnswers: 2, hasPitch: true, jobs: ["closer-apex"], status: "New", decision: "Pending", stage: "New" },
  { name: "Hassan Rafiq", location: "London, UK", years: "10 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["enterprise-ae-halden", "bdm-anon-fintech"], status: "Shortlisted", decision: "Approved", stage: "Put forward" },
  { name: "Georgia Pike", location: "Bristol, UK", years: "No sales experience yet", voiceAnswers: 0, hasPitch: false, jobs: ["sdr-northline"], status: "Rejected", decision: "Rejected", stage: "Not approved" },
  { name: "Luke Fenton", location: "Birmingham, UK", years: "3 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["field-sales-vertex", "inside-sales-lumen"], status: "New", decision: "Pending", stage: "New" },
  { name: "Nadia Sørensen", location: "London, UK", years: "5 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["ae-brightpath", "sales-manager-orbit"], status: "Shortlisted", decision: "Approved", stage: "Approved" },
  { name: "Callum Reid", location: "Glasgow, UK", years: "1 year in sales", voiceAnswers: 2, hasPitch: true, jobs: ["inside-sales-lumen"], status: "New", decision: "Pending", stage: "New" },
  { name: "Bianca Ferrari", location: "Remote, UK", years: "6 years in sales", voiceAnswers: 3, hasPitch: true, jobs: ["closer-apex", "ae-brightpath"], status: "New", decision: "Approved", stage: "Approved" },
];

const emailOf = (name: string) =>
  `${name.toLowerCase().normalize("NFD").replace(/[^a-z ]/g, "").split(" ").join(".")}@email.com`;

const phoneOf = (i: number) => `+44 7700 9${String(100 + i * 7).padStart(5, "0")}`;

export const demoApplications: Application[] = people.flatMap((p, i) =>
  p.jobs.map((jobId, k) => ({
    id: `${jobId}-demo-${i}-${k}`,
    jobId,
    name: p.name,
    email: emailOf(p.name),
    phone: phoneOf(i),
    location: p.location,
    years: p.years,
    cvName: `${p.name.split(" ")[0]?.toLowerCase()}-cv.pdf`,
    hasPitch: p.hasPitch,
    voiceAnswers: p.voiceAnswers,
    status: p.status,
    appliedAt: daysAgo(1 + ((i * 3 + k) % 14)).slice(0, 10),
  })),
);

export const demoAccount: EmployerAccount = {
  company: "Northline Group",
  contactName: "Sarah Whitcombe",
  email: "sarah@northlinegroup.co.uk",
  phone: "+44 20 7946 0912",
  username: "northline-group-482",
  password: "pipeline-8xk2-482",
  createdAt: daysAgo(30),
};

/**
 * The funnel role. One strong advert pulls the volume in, then the team grades
 * everyone and splits them across the campaigns that match their money.
 */
export const funnelJobId = "closdd-sales-funnel";

export const demoFunnelJob: EmployerJob = {
  id: funnelJobId,
  ref: "CL-0001",
  title: "Sales Executive (multiple clients)",
  seniority: "Mid-level",
  contract: "Full-time",
  company: "CLOSDD exclusive",
  anonymous: true,
  location: "UK wide",
  setting: "Hybrid",
  payStructure: "Base salary + commission",
  pay: "£25,000 to £45,000 base + uncapped commission",
  advert:
    "We are hiring salespeople for several of our clients at once. Apply here, record a 60 second pitch, and we will put you in front of the client whose product, patch and money fit you best.\n\nB2B, B2C, field, inbound and outbound roles available across the UK.",
  tags: ["urgent", "multiple-openings", "uncapped-commission"],
  status: "Live",
  createdAt: daysAgo(9),
  exclusive: true,
};

type FunnelSeed = { name: string; location: string; years: string; segs: PoolTag["segments"]; min: number; max: number; grade: PoolTag["grade"]; voiceAnswers: number; hasPitch: boolean; days: number };

const funnelPeople: FunnelSeed[] = [
  { name: "Kayleigh Munro", location: "Glasgow, UK", years: "2 years in sales", segs: ["B2C", "Inbound"], min: 25, max: 30, grade: "A", voiceAnswers: 3, hasPitch: true, days: 1 },
  { name: "Femi Adeyemi", location: "London, UK", years: "5 years in sales", segs: ["B2B", "Outbound"], min: 40, max: 50, grade: "A", voiceAnswers: 3, hasPitch: true, days: 1 },
  { name: "Sophie Tran", location: "Manchester, UK", years: "3 years in sales", segs: ["B2C", "Field sales"], min: 28, max: 34, grade: "B", voiceAnswers: 3, hasPitch: true, days: 2 },
  { name: "Dominic Shaw", location: "Leeds, UK", years: "7 years in sales", segs: ["B2B", "Account management"], min: 50, max: 60, grade: "A", voiceAnswers: 3, hasPitch: true, days: 2 },
  { name: "Aisha Karim", location: "Birmingham, UK", years: "1 year in sales", segs: ["B2C"], min: 22, max: 26, grade: "B", voiceAnswers: 2, hasPitch: true, days: 3 },
  { name: "Craig Bennett", location: "Bristol, UK", years: "4 years in sales", segs: ["Field sales", "B2B"], min: 30, max: 36, grade: "B", voiceAnswers: 3, hasPitch: true, days: 3 },
  { name: "Leah Osborne", location: "Remote, UK", years: "6 years in sales", segs: ["B2B", "Outbound"], min: 45, max: 55, grade: "A", voiceAnswers: 3, hasPitch: true, days: 4 },
  { name: "Marcin Nowak", location: "London, UK", years: "2 years in sales", segs: ["Inbound", "B2C"], min: 26, max: 30, grade: "Unsorted", voiceAnswers: 2, hasPitch: true, days: 5 },
  { name: "Tanya Beckles", location: "Nottingham, UK", years: "3 years in sales", segs: [], min: 0, max: 0, grade: "Unsorted", voiceAnswers: 1, hasPitch: false, days: 5 },
  { name: "Owen Pritchard", location: "Cardiff, UK", years: "8 years in sales", segs: ["B2B", "Account management"], min: 55, max: 70, grade: "C", voiceAnswers: 3, hasPitch: true, days: 6 },
  { name: "Nicole Ferreira", location: "Liverpool, UK", years: "2 years in sales", segs: ["B2C", "Outbound"], min: 25, max: 28, grade: "A", voiceAnswers: 3, hasPitch: true, days: 7 },
  { name: "Jack Hollins", location: "Newcastle, UK", years: "No sales experience yet", segs: [], min: 0, max: 0, grade: "Unsorted", voiceAnswers: 0, hasPitch: false, days: 8 },
];

export const demoFunnelApplications: Application[] = funnelPeople.map((p, i) => ({
  id: `${funnelJobId}-demo-${i}`,
  jobId: funnelJobId,
  name: p.name,
  email: emailOf(p.name),
  phone: phoneOf(20 + i),
  location: p.location,
  years: p.years,
  cvName: `${p.name.split(" ")[0]?.toLowerCase()}-cv.pdf`,
  hasPitch: p.hasPitch,
  voiceAnswers: p.voiceAnswers,
  status: "New",
  appliedAt: daysAgo(p.days).slice(0, 10),
}));

const demoPool: Record<string, PoolTag> = Object.fromEntries(
  funnelPeople.map((p) => [
    emailOf(p.name).toLowerCase(),
    { segments: p.segs, min: p.min, max: p.max, grade: p.grade } as PoolTag,
  ]),
);

const keyOf = (p: PersonSeed) => emailOf(p.name).toLowerCase();

const distributionSeeds: { person: string; jobId: string; status: DistributionStatus; days: number }[] = [
  { person: "Jordan Miles", jobId: "ae-brightpath", status: "Shortlisted", days: 3 },
  { person: "Jordan Miles", jobId: "bdm-anon-fintech", status: "Viewed", days: 2 },
  { person: "Amara Bello", jobId: "ae-brightpath", status: "Interview", days: 5 },
  { person: "Ryan Doyle", jobId: "closer-apex", status: "Hired", days: 9 },
  { person: "Tom Whitfield", jobId: "field-sales-vertex", status: "Sent", days: 1 },
  { person: "Chloe Berrington", jobId: "sales-manager-orbit", status: "Interview", days: 4 },
  { person: "Danny Okafor", jobId: "bdm-anon-fintech", status: "Shortlisted", days: 6 },
  { person: "Danny Okafor", jobId: "enterprise-ae-halden", status: "Rejected", days: 12 },
  { person: "Hassan Rafiq", jobId: "enterprise-ae-halden", status: "Interview", days: 7 },
  { person: "Nadia Sørensen", jobId: "sales-manager-orbit", status: "Viewed", days: 2 },
];

const activitySeeds: { message: string; hours: number }[] = [
  { message: "Marked Ryan Doyle as hired for High Ticket Closer at Apex Coaching", hours: 3 },
  { message: "Put Hassan Rafiq forward to Enterprise Account Executive at Halden Systems", hours: 6 },
  { message: "Approved candidate Bianca Ferrari", hours: 9 },
  { message: "Approved application from Nadia Sørensen for Account Executive", hours: 22 },
  { message: "Did not approve candidate Georgia Pike", hours: 27 },
  { message: "Approved role Sales Development Representative at Northline Group", hours: 31 },
  { message: "Saved Ellie Vance for a future role", hours: 44 },
  { message: "Approved employer Northline Group", hours: 52 },
  { message: "Moved Amara Bello to interviewed", hours: 66 },
  { message: "Approved role Business Development Manager at Confidential fintech", hours: 74 },
];

export function demoAdminStore(): Partial<AdminStore> {
  const jobs: Record<string, Decision> = {};
  demoJobs.forEach((j, i) => (jobs[j.id] = i === 0 ? "Approved" : i < 6 ? "Approved" : i === 6 ? "Pending" : "Approved"));

  const candidates: Record<string, Decision> = {};
  const stagesMap: Record<string, Stage> = {};
  for (const p of people) {
    candidates[keyOf(p)] = p.decision;
    stagesMap[keyOf(p)] = p.stage;
  }

  const applications: Record<string, Decision> = {};
  demoApplications.forEach((a, i) => {
    const person = people.find((p) => p.name === a.name)!;
    applications[a.id] = person.decision === "Rejected" ? "Rejected" : person.decision === "Pending" ? "Pending" : i % 7 === 3 ? "Pending" : "Approved";
  });

  const byName = new Map(people.map((p) => [p.name, p] as const));
  const jobLookup = new Map(demoJobs.map((j) => [j.id, j] as const));
  const distributions: Distribution[] = distributionSeeds.map((d, i) => {
    const job = jobLookup.get(d.jobId)!;
    return {
      id: `dist-demo-${i}`,
      candidateKey: keyOf(byName.get(d.person)!),
      candidateName: d.person,
      jobId: d.jobId,
      jobTitle: job.title,
      employer: job.anonymous ? "Anonymous employer" : job.company,
      status: d.status,
      sentAt: daysAgo(d.days),
    };
  });

  const activity: ActivityEntry[] = activitySeeds.map((a, i) => ({
    id: `act-demo-${i}`,
    at: new Date(Date.now() - a.hours * 3600000).toISOString(),
    actor: "Admin",
    message: a.message,
  }));

  return {
    jobs,
    applications,
    employers: { "Northline Group": "Approved" },
    candidates,
    stages: stagesMap,
    distributions,
    activity,
    saved: { [demoApplications[demoApplications.length - 1]!.id]: true },
    pool: demoPool,
  };
}
